import type { MessageKey } from '@dijitalapp/i18n';
import { ERROR_CODES, type ApiErrorBody, type ErrorCode } from '@dijitalapp/shared';

/** Client-side error codes in addition to the server taxonomy. */
export type ClientErrorCode = ErrorCode | 'NETWORK';

export class ApiError extends Error {
  constructor(
    readonly code: ClientErrorCode,
    readonly status: number,
    readonly fields?: Record<string, string>,
    readonly requestId?: string,
  ) {
    super(code);
  }
}

const KNOWN = new Set<string>(ERROR_CODES);

/** Maps any thrown value to the i18n key of a user-facing message (never raw server text). */
export function errorMessageKey(err: unknown): MessageKey {
  if (err instanceof ApiError) {
    if (err.code === 'NETWORK') return 'error.NETWORK';
    if (KNOWN.has(err.code)) return `error.${err.code}` as MessageKey;
  }
  return 'error.UNKNOWN';
}

export function parseErrorBody(status: number, body: unknown): ApiError {
  const e = (body as Partial<ApiErrorBody> | null)?.error;
  const code = e?.code && KNOWN.has(e.code) ? e.code : 'UNKNOWN';
  return new ApiError(code, status, e?.fields, e?.requestId);
}

export async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  if (res.status === 204) return undefined as T;
  const json: unknown = await res.json().catch(() => null);
  if (!res.ok) throw parseErrorBody(res.status, json);
  return json as T;
}
